import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Wrench, Battery, Zap, Package, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Testimonials from "@/components/Testimonials";
import FeaturedReviews from "@/components/FeaturedReviews";
import FAQSection from "@/components/FAQSection";
import { useLang } from "@/context/LanguageContext";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const { t } = useLang();

  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then((r) => setFeatured(r.data))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6 animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-zinc-200 px-3 py-1.5 text-xs font-semibold text-zinc-600">
              <span className="w-1.5 h-1.5 rounded-full bg-brand" /> {t("home.badge")}
            </div>
            <h1 className="mt-6 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-brand leading-[1.05]">
              {t("home.heroTitle1")}
              <br />
              <span className="text-brand">{t("home.heroTitle2")}</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-zinc-600 max-w-lg leading-relaxed">
              {t("home.heroSubtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/services">
                <Button data-testid="hero-services-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white px-6 py-6 text-base font-semibold">
                  {t("home.repairServices")} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/shop">
                <Button data-testid="hero-shop-btn" variant="outline" className="rounded-full border-2 border-zinc-200 hover:border-zinc-900 text-zinc-900 px-6 py-6 text-base font-semibold bg-white">
                  {t("home.shopBatteries")}
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-zinc-500">
              <div className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-brand" /> {t("home.warranty")}</div>
              <div className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-brand" /> {t("home.turnaround")}</div>
              <div className="flex items-center gap-1.5"><Wrench className="w-4 h-4 text-brand" /> {t("home.certified")}</div>
            </div>
          </div>

          <div className="md:col-span-6 relative">
            <div className="aspect-[4/5] max-w-md ml-auto rounded-[2rem] overflow-hidden product-image-bg shadow-[0_30px_80px_-20px_rgba(0,0,0,0.15)]">
              <img
                src="https://images.unsplash.com/photo-1606841837239-c5a1a4a07af7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHx3aXJlbGVzcyUyMGVhcmJ1ZHN8ZW58MHx8fHwxNzg2MzI1NTIxfDA&ixlib=rb-4.1.0&q=85"
                alt="Wireless earbuds"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="hidden md:block absolute -left-4 bottom-8 bg-white rounded-2xl shadow-xl border border-zinc-100 p-4 w-64 animate-fade-up" style={{ animationDelay: "0.2s" }}>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{t("home.thisWeek")}</div>
              <div className="mt-1 font-display font-bold text-zinc-900 text-lg">{t("home.repairs")}</div>
              <div className="mt-2 flex items-center gap-2 text-xs text-zinc-500">
                <div className="flex -space-x-1.5">
                  <div className="w-6 h-6 rounded-full bg-brand" />
                  <div className="w-6 h-6 rounded-full bg-zinc-300" />
                  <div className="w-6 h-6 rounded-full bg-zinc-800" />
                </div>
                {t("home.happyCustomers")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { title: t("home.catRepairTitle"), desc: t("home.catRepairDesc"), icon: Wrench, href: "/services", key: "repair" },
            { title: t("home.catBatteriesTitle"), desc: t("home.catBatteriesDesc"), icon: Battery, href: "/shop?category=Batteries", key: "batteries" },
            { title: t("home.catPartsTitle"), desc: t("home.catPartsShortDesc"), icon: Package, href: "/shop?category=Parts", key: "parts" },
            { title: t("home.catAccessoriesTitle"), desc: t("home.catAccessoriesShortDesc"), icon: Sparkles, href: "/shop?category=Accessories", key: "accessories" },
          ].map((c) => (
            <Link key={c.key} to={c.href} data-testid={`cat-card-${c.key}`} className="group card-lift bg-white rounded-3xl border border-zinc-100 p-8 flex items-start gap-5">
              <div className="w-12 h-12 rounded-2xl bg-[#FBE8EC] text-brand flex items-center justify-center flex-shrink-0">
                <c.icon className="w-6 h-6" />
              </div>
              <div>
                <div className="font-display font-bold text-lg text-zinc-900">{c.title}</div>
                <div className="text-sm text-zinc-500 mt-1 leading-relaxed">{c.desc}</div>
                <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand group-hover:gap-2 transition-all">
                  {t("home.explore")} <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured products */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-14">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{t("home.featured")}</div>
            <h2 className="mt-2 font-display font-bold text-3xl sm:text-4xl tracking-tight text-zinc-900">{t("home.featuredTitle")}</h2>
          </div>
          <Link to="/shop" data-testid="view-all-btn" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-zinc-900 hover:text-brand transition-colors">
            {t("home.viewAll")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {featured.length === 0 ? (
          <div className="text-sm text-zinc-500">{t("home.loading")}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* Featured customer reviews (shop-wide) */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-4">
        <FeaturedReviews limit={6} minRating={4} headingKey="reviews.homeHeading" subKey="reviews.homeSub" />
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* FAQ */}
      <FAQSection />

      {/* Trust band */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="rounded-3xl bg-zinc-900 text-white p-10 md:p-16 relative overflow-hidden">
          <div className="relative z-10 max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{t("home.guaranteed")}</div>
            <h3 className="mt-3 font-display font-extrabold text-3xl md:text-4xl tracking-tight leading-tight">
              {t("home.trustTitle")}
            </h3>
            <p className="mt-4 text-zinc-300 leading-relaxed">
              {t("home.trustCopy")}
            </p>
            <Link to="/services">
              <Button data-testid="learn-more-btn" className="mt-6 rounded-full bg-white text-zinc-900 hover:bg-zinc-100 font-semibold px-6">
                {t("home.learnMore")} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </div>
          <div className="absolute -right-16 -bottom-16 w-96 h-96 rounded-full bg-brand/20 blur-3xl" />
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-brand/30 blur-2xl" />
        </div>
      </section>
    </main>
  );
}
